// FriendRequestButton.tsx
import { useEffect } from 'react';
import { Button, Reveal, Icon } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';

interface Props {
  userId: string;
  onStatusChange?: () => void;
}

export default observer(function FriendRequestButton({ userId, onStatusChange }: Props) {
  const { friendRequestStore } = useStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (!friendRequestStore.getFriendshipStatus(userId)) {
      friendRequestStore.loadFriendshipStatus(userId);
    }
  }, [userId, friendRequestStore]);

  const friendshipStatus = friendRequestStore.getFriendshipStatus(userId);
  const loading = friendRequestStore.isLoading(userId);

  const handleSendRequest = async () => {
    try {
      await friendRequestStore.sendFriendRequest(userId);
      onStatusChange?.();
      toast.success(t('friendRequestButton.requestSent'));
    } catch (error: any) {
      toast.error(error?.response?.data || t('friendRequestButton.requestSendFailed'));
    }
  };

  const handleCancelRequest = async () => {
    if (!friendshipStatus?.requestId) return;
    try {
      await friendRequestStore.cancelFriendRequest(userId, friendshipStatus.requestId);
      onStatusChange?.();
      toast.success(t('friendRequestButton.requestCancelled'));
    } catch (error) {
      toast.error(t('friendRequestButton.requestCancelFailed'));
    }
  };

  const handleAcceptRequest = async () => {
    if (!friendshipStatus?.requestId) return;
    try {
      await friendRequestStore.acceptFriendRequest(userId, friendshipStatus.requestId);
      onStatusChange?.();
      toast.success(t('friendRequestButton.requestAccepted'));
    } catch (error) {
      toast.error(t('friendRequestButton.requestAcceptFailed'));
    }
  };

  const handleRejectRequest = async () => {
    if (!friendshipStatus?.requestId) return;
    try {
      await friendRequestStore.rejectFriendRequest(userId, friendshipStatus.requestId);
      onStatusChange?.();
      toast.success(t('friendRequestButton.requestRejected'));
    } catch (error) {
      toast.error(t('friendRequestButton.requestRejectFailed'));
    }
  };

  const handleUnfriend = async () => {
    try {
      await friendRequestStore.unfriend(userId);
      onStatusChange?.();
      toast.success(t('friendRequestButton.unfriended'));
    } catch (error) {
      toast.error(t('friendRequestButton.unfriendFailed'));
    }
  };

  if (!friendshipStatus || friendshipStatus.status === 'self') return null;

  const buttonStyle = {
    fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
    padding: 'clamp(0.5rem, 1.5vw, 0.8rem) clamp(0.8rem, 2vw, 1rem)',
    width: '100%',
    minWidth: '0',
    maxWidth: '100%'
  };

  const smallButtonStyle = {
    fontSize: 'clamp(0.65rem, 1.8vw, 0.85rem)',
    padding: 'clamp(0.5rem, 1.5vw, 0.8rem) clamp(0.5rem, 1.5vw, 0.7rem)',
    flex: 1,
    whiteSpace: 'nowrap' as const
  };

  const renderButton = () => {
    switch (friendshipStatus.status) {
      case 'none':
        return (
          <Button
            color="blue"
            content={t('friendRequestButton.sendRequest')}
            loading={loading}
            onClick={handleSendRequest}
            style={buttonStyle}
          />
        );

      case 'pending_sent':
        return (
          <Reveal animated="move">
            <Reveal.Content visible style={{ width: '100%' }}>
              <Button
                color="blue"
                content={t('friendRequestButton.requestSentLabel')}
                style={buttonStyle}
              />
            </Reveal.Content>
            <Reveal.Content hidden style={{ width: '100%' }}>
              <Button
                color="red"
                content={t('friendRequestButton.cancelRequest')}
                loading={loading}
                onClick={handleCancelRequest}
                style={buttonStyle}
              />
            </Reveal.Content>
          </Reveal>
        );

      case 'pending_received':
        return (
          <div style={{ width: '100%' }}>
            <div style={{ 
              fontSize: 'clamp(0.7rem, 1.8vw, 0.85rem)',
              fontWeight: '600',
              color: '#2185d0',
              marginBottom: '0.4em',
              textAlign: 'center'
            }}>
              {t('friendRequestButton.friendRequest')}
            </div>
            <div style={{ display: 'flex', gap: '0.4em', width: '100%' }}>
              <Button
                color="green"
                loading={loading}
                onClick={handleAcceptRequest}
                style={smallButtonStyle}
              >
                <Icon name="check" /> {t('friendRequestButton.accept')}
              </Button>
              <Button
                color="red"
                loading={loading}
                onClick={handleRejectRequest}
                style={smallButtonStyle}
              >
                <Icon name="close" /> {t('friendRequestButton.reject')}
              </Button>
            </div>
          </div>
        );

      case 'friends':
        return (
          <Reveal animated="move">
            <Reveal.Content visible style={{ width: '100%' }}>
              <Button
                color="blue"
                style={buttonStyle}
              >
                <Icon name="check circle" /> {t('friendRequestButton.friends')}
              </Button>
            </Reveal.Content>
            <Reveal.Content hidden style={{ width: '100%' }}>
              <Button
                color="red"
                loading={loading}
                onClick={handleUnfriend}
                style={buttonStyle}
              >
                <Icon name="close" /> {t('friendRequestButton.unfriend')}
              </Button>
            </Reveal.Content>
          </Reveal>
        );

      default:
        return null;
    }
  };

  return renderButton();
});